import { useMemo, useState } from "react";
import { FaSync, FaServer } from "react-icons/fa";
import {
  ListSkeleton,
  EmptyState,
  ListView,
  ListToolbar,
} from "../../shared";
import { Pagination } from "../../../Components/atoms";
import { useDebounce } from "../../../Hooks/useDebounce";
import useAuth from "../../../Hooks/useAuth";
import { useGetEmqxClientsQuery } from "../../../store";

const PAGE_SIZE = 50;

const StatusBadge = ({ connected }) =>
  connected ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-xs font-medium">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
      Online
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-gray-50 text-gray-600 border border-gray-200 text-xs">
      Offline
    </span>
  );

const COLUMNS = [
  {
    key: "clientid",
    label: "Client ID",
    cellClassName: "font-mono text-xs text-gray-900",
  },
  { key: "username", label: "Username" },
  {
    key: "ip_address",
    label: "IP",
    cellClassName: "font-mono text-xs text-gray-600",
  },
  {
    key: "connected_at",
    label: "Bağlandı",
    render: (r) =>
      r.connected_at ? new Date(r.connected_at).toLocaleString("tr-TR") : "—",
  },
  {
    key: "keepalive",
    label: "Keepalive",
    render: (r) => (r.keepalive ? `${r.keepalive}s` : "—"),
  },
  {
    key: "connected",
    label: "Durum",
    render: (r) => <StatusBadge connected={r.connected} />,
  },
];

export default function ClientsTab() {
  const { auth } = useAuth();
  const token = auth?.accessToken;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  const {
    data: response,
    isLoading,
    isFetching,
    refetch,
  } = useGetEmqxClientsQuery(
    {
      token,
      page,
      limit: PAGE_SIZE,
      clientid: debouncedSearch || undefined,
    },
    { pollingInterval: 15_000 }
  );

  const clients = useMemo(() => response?.data?.data || [], [response]);
  const meta = response?.data?.meta || {};
  const totalCount = meta.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div className="flex flex-col h-full min-h-0 bg-white">
      <ListToolbar
        className="px-3 pt-3 pb-2.5 sm:px-4 sm:pt-4 sm:pb-4 md:px-6"
        count={totalCount}
        countLabel="Client"
        countIcon={FaServer}
        isLoading={isFetching}
        actionItems={[
          {
            label: "Yenile",
            icon: FaSync,
            onClick: refetch,
            disabled: isFetching,
          },
        ]}
        searchFields={[
          {
            placeholder: "Client ID ara...",
            value: search,
            onChange: (e) => {
              setSearch(e.target.value);
              setPage(1);
            },
          },
        ]}
        onClear={() => {
          setSearch("");
          setPage(1);
        }}
      />

      <ListView
        columns={COLUMNS}
        data={clients}
        rowKey="clientid"
        renderMobileCard={(r) => (
          <div className="bg-white px-3.5 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="font-mono text-xs font-semibold text-gray-900 truncate">
                {r.clientid}
              </span>
              <StatusBadge connected={r.connected} />
            </div>
            <div className="text-xs text-gray-500 truncate">{r.username || "—"}</div>
            <div className="flex items-center justify-between mt-1.5 text-xs text-gray-400">
              <span className="font-mono">{r.ip_address || "—"}</span>
              <span>
                {r.connected_at
                  ? new Date(r.connected_at).toLocaleString("tr-TR")
                  : "—"}
              </span>
            </div>
          </div>
        )}
        isLoading={isLoading}
        loadingNode={
          <div className="relative p-3 sm:p-4 md:p-6">
            <ListSkeleton message="Bağlı cihazlar yükleniyor..." />
          </div>
        }
        empty={
          <div className="p-4 md:p-6">
            <EmptyState
              title="Bağlı cihaz yok"
              description="EMQX broker'a o anda hiç MQTT client bağlı değil."
            />
          </div>
        }
        footer={
          totalPages > 1 ? (
            <div className="shrink-0 border-t border-gray-200 bg-white">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                itemsPerPage={PAGE_SIZE}
                totalItems={totalCount}
                showInfo={true}
                disabled={isFetching}
              />
            </div>
          ) : null
        }
      />
    </div>
  );
}
